import { bottomLevel } from "building-generation/elements/level/levelTools";
import { iterateBuildingGeometries } from "building-geometry/geometry/buildingGeometry";
import type { Building } from "building-types/building/buildingTypes";
import { lineRenderGeometryOptionsNew } from "building-types/geometry/buildingGeometryTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import {
  defaultIterateFaceCategories,
  doorFaceCategories,
  FaceCategory,
  propFaceCategories,
} from "building-types/materials/faceCategories";
import type { PropWithAssetId } from "building-types/props/propTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { ViewEntityTable } from "building-types/views/viewEntityTable";
import { GROUND_LEVEL } from "common/constants/buildingObjects";
import type { RWTable } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { aabb2Union } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import { aabb3New, type AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import {
  addOcclusionPolygonToBSP,
  buildBSPNode,
  buildBSPNodeFromAABB3,
  buildPolygonEdgePlanes,
  buildPolygonsFromFaces,
  clipLinesOutsideConvexVol,
  clipPolygonOutsideConvexVol,
  polygonBSPFlip,
  polygonBSPNew,
  removeCoplanarLinesBSP,
  renderNodeBSP,
  splitLineBSP,
  splitPolygonBSP,
  splitPolygonPlaneBSP,
} from "geom/bsp";
import { clipFace, clippingNodeNew } from "geom/bspClipping";
import { AllocatorsBSP, getBSPAllocators, setBSPAllocators } from "geom/bspMem";
import type { LineBSP, NodeBSP, PlaneBSP, PolygonBSP, RenderInfoBSP } from "geom/bspTypes";
import { BSP_EPSILON, REMOVE_COPLANAR_LINES_EPSILON, TraversalCullingBSP, TraversalOrderBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, DIST_Z_FIGHT } from "geom/core";
import { extrusionToBRep } from "geom/extrude";
import { fillHatchLines } from "geom/hatch";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { hatchProceduralMaterialNew } from "geom/hatchTypes";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { lineBSPNew } from "geom/lineBSPNew";
import type { Mat4 } from "geom/mat4";
import { mat4Dot, mat4Invert, mat4Mult } from "geom/mat4";
import { buildPolygonsFromMesh } from "geom/meshBSP";
import { offsetPolyLine2 } from "geom/offset";
import { planeEval, planeMoveAlongNormal, planeNewXY, planeProjectPolyLine3, planeTransform } from "geom/plane";
import { planeBSPByThreePoints, planeBSPFlip, planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2FromAABB2,
  polyLine2MergeCoincidentVertices,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Close, polyLine3New } from "geom/polyLine3";
import { pushXY } from "geom/utils";
import { UVGeneration } from "geom/uv";
import { vec2DistSquared, vec2IsBetween } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Dot, vec3Normalize } from "geom/vec3";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import { buildPolygonsFromFaceGroups, faceGroupBSPNew } from "lib/house/faceGroupBSP";
import { buildOpeningOccluderFaceGroups } from "lib/house/pdf/openingOcclusionFaces";
import { makeReportingUnitBuildingFaceFilter } from "lib/house/plans/reportingUnitFilterUtils";
import {
  getFoundationFaceReportingUnitResolver,
  resolveFoundationFaceReportingUnitLabelId,
} from "lib/house/plans/reportingUnitFoundationAttribution";
import { collectBuildingMeshes, MeshNamingConvention } from "./collectBuildingFaces";
import { defaultHatchMaterialFactory } from "./hatchMaterialFactory";
import {
  DETAIL_FACES_PRIORITY,
  GROUND_FACES_PRIORITY,
  POLYGON_OFFSET_FOR_CROSS_SECTION_UNION,
  PROPS_FACES_PRIORITY,
  REGULAR_FACES_PRIORITY,
} from "./perspectiveConstants";
import type { CollectedBuildingMesh, LazyPropMesh } from "./perspectiveTypes";
import type { RenderedPerspectiveLines } from "./RenderedPerspectiveLines";
import { renderedPerspectiveLinesNew } from "./RenderedPerspectiveLines";
import type { SceneBSP } from "./SceneBSP";

const UNDEFINED_MATERIAL_KEY = "undefined";
const USE_BSP_OBJECT_POOLS_BY_DEFAULT = false;

interface PerspectiveDrawingBSP {
  sceneBSP: SceneBSP;
  occlusionBSP: NodeBSP | null;
  roofVolumesBSP: NodeBSP | null;
  viewMatrix: Mat4;
  isPerspectiveMatrix: boolean;
  projectionMatrix: Mat4;
  clippingPlanes?: PlaneBSP[];
  isPlanView?: boolean;
  isElevation?: boolean;
  enableShading?: boolean;
}

export interface RenderPerspectiveLinesOptions {
  sceneBSP: SceneBSP;
  occlusionBSP?: NodeBSP;

  // Currently we don't have any roof framing and roof cross sections look empty and weird on plans,
  // so we decided to add those volumes for filling the roofs on the section plan pages.
  roofVolumesBSP?: NodeBSP;

  viewMatrix: Mat4;
  isPerspectiveMatrix: boolean;
  prjMatrix: Mat4;
  clippingPlanes?: PlaneBSP[];
  sectionClippingPlane?: Plane;
  isPlanView?: boolean;
  isElevation?: boolean; // Defaults to false
  epsilon?: number;
  groundZ?: number;
  useBSPObjectPools?: boolean;
  /** for hydrating error reporting with entity information */
  entityTable?: ViewEntityTable;
}

interface RenderPerspectiveShadedPolygonsOptions {
  sceneBSP: SceneBSP;
  viewMatrix: Mat4;
  isPerspectiveMatrix: boolean;
  prjMatrix: Mat4;
  lightDirX: number;
  lightDirY: number;
  lightDirZ: number;
  lightIntensity?: number;
  ambientIntensity?: number;
  clippingPlanes?: PlaneBSP[];
  isPlanView?: boolean;
  epsilon?: number;
  useBSPObjectPools?: boolean;
  /** for hydrating error reporting with entity information */
  entityTable?: ViewEntityTable;
}

interface RenderPerspectivePolygonsOptions {
  sceneBSP: SceneBSP;
  viewMatrix: Mat4;
  isPerspectiveMatrix: boolean;
  prjMatrix: Mat4;
  clippingPlanes?: PlaneBSP[];
  isPlanView?: boolean;
  epsilon?: number;
  useBSPObjectPools?: boolean;
  /** for hydrating error reporting with entity information */
  entityTable?: ViewEntityTable;
  polygonFilter?: (PolygonBSP) => boolean;
  getUserData?: (PolygonBSP) => string;
}

export interface ShadedPolygon {
  loop: PolyLine2;
  shadingFactor: number;
}

export interface PerspectiveDrawing extends PerspectiveDrawingBSP {
  groundZ: number;
}

function shadedPolygonNew(loop: PolyLine2, shadingFactor: number): ShadedPolygon {
  return {
    loop,
    shadingFactor,
  };
}

export function getPolygonsAabb(
  polygons: PolygonBSP[],
  filterPolygon: ((polygon: PolygonBSP) => boolean) | null = null,
): AABB3 {
  let minX = Number.MAX_VALUE;
  let minY = Number.MAX_VALUE;
  let minZ = Number.MAX_VALUE;

  let maxX = -Number.MAX_VALUE;
  let maxY = -Number.MAX_VALUE;
  let maxZ = -Number.MAX_VALUE;

  for (let i = 0, l = polygons.length; i < l; i++) {
    const polygon = polygons[i];
    const { vertices } = polygon;
    if (filterPolygon && !filterPolygon(polygon)) {
      continue;
    }

    for (let v = 0, lv = vertices.length; v < lv; v += 3) {
      const x = vertices[v];
      const y = vertices[v + 1];
      const z = vertices[v + 2];

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      minZ = Math.min(minZ, z);

      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
      maxZ = Math.max(maxZ, z);
    }
  }

  const validAabb = maxX >= minX && maxY >= minY && maxZ >= minZ;
  return validAabb ? aabb3New(minX, minY, minZ, maxX, maxY, maxZ) : aabb3New(0, 0, 0, 0, 0, 0);
}

function nonGroundPolygon(polygon: PolygonBSP): boolean {
  return polygon.priority !== GROUND_FACES_PRIORITY;
}

export function getBuildingFoundationZ(building: Building) {
  if (!building) {
    return GROUND_LEVEL;
  }

  const bottom = bottomLevel(building.levels);

  if (!bottom) {
    return GROUND_LEVEL;
  }

  return bottom.floorElevation + bottom.levelDatumsGenerated.foundation - DIST_Z_FIGHT;
}

export function sceneBSPEmpty(): SceneBSP {
  return {
    aabb: aabb3New(0, 0, 0, 0, 0, 0),
    root: null,
  };
}

export interface BuildingBSPNewOptions {
  building: Building;
  faceCategoryFilter?: Set<FaceCategory>;
  hatchCategoryFilter?: Set<FaceCategory>;
  visibilitySelectors?: SheetGeometrySelectorEffectMap[];
  enableHatchMaterials?: boolean;
  entityTable?: ViewEntityTable;
  addEntityIds?: boolean;
  removeCoplanarLines?: boolean; // Defaults to true
  useBSPObjectPools?: boolean;
  /**
   * Seal openings with invisible occluders. Honored by `collectBuildingViewFaceGroups`; silently
   * ignored by `buildingBSPNew` (the JS-BSP path, which doesn't read this flag).
   */
  fillOpeningOccluders?: boolean;
  selectedReportingUnitLabelId?: string;
}

enum BSPPriority {
  DEFAULT,
  GROUND,
  DETAIL,
}

function faceCategoryBSPPriority(category: FaceCategory): BSPPriority {
  switch (category) {
    case FaceCategory.GROUND:
      return BSPPriority.GROUND;

    case FaceCategory.ROOF_RIDGE_VENT:
    case FaceCategory.ROOF_RIDGE_CAP:
    case FaceCategory.ROOF_FRIEZE_BOARD:
    case FaceCategory.ROOF_FASCIA_BOARD:
    case FaceCategory.ROOF_FASCIA_TOP_TRIM:
    case FaceCategory.ROOF_SOFFIT:
    case FaceCategory.ROOF_BELLY_BAND_BOARD:
    case FaceCategory.ROOF_WALL_BASE_BOARD:
    case FaceCategory.ROOF_EXPOSED_RAFTER:
    case FaceCategory.ROOF_GABLE_VENT:
    case FaceCategory.ROOF_WALL_TRIM:
    case FaceCategory.ROOF_FRAMING:
    case FaceCategory.RAILING:
    case FaceCategory.WAINSCOTING:
    case FaceCategory.WAINSCOTING_TRIM:
    case FaceCategory.FOUNDATION_GARAGE_CURB:
    case FaceCategory.FOUNDATION_FOOTING:
    case FaceCategory.FOUNDATION_PIER:
    case FaceCategory.WINDOW_EXTERIOR:
    case FaceCategory.WINDOW_INTERIOR:
    case FaceCategory.WINDOW_SCREEN:
    case FaceCategory.WINDOW_GLASS:
    case FaceCategory.WINDOW_GRID:
    case FaceCategory.WINDOW_DECORATION:
    case FaceCategory.DOOR_EXTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_EXTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_INTERIOR:
    case FaceCategory.DOOR_INTERIOR:
    case FaceCategory.DOOR_DECORATION:
    case FaceCategory.CASEWORK:
    case FaceCategory.PORCH_DECORATION:
    case FaceCategory.CORNER_BOARD:
    case FaceCategory.FLOOR:
    case FaceCategory.FLOOR_FRAMING:
    case FaceCategory.SUB_FLOORING:
    case FaceCategory.SLAB_FLOORING:
    case FaceCategory.BASE_TRIM_MOLDING:
    case FaceCategory.CROWN_TRIM_MOLDING:
    case FaceCategory.WALL_PLANE_TRIM_MOLDING:
    case FaceCategory.CEILING:
    case FaceCategory.SKYLIGHT_DECORATION:
    case FaceCategory.SKYLIGHT_EXTERIOR:
    case FaceCategory.SKYLIGHT_GLASS:
      return BSPPriority.DETAIL;

    case FaceCategory.ROOF_TOP:
    case FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY:
    case FaceCategory.ROOF_BOTTOM:
    case FaceCategory.ROOF_DORMER_WALL:
    case FaceCategory.ROOF_WALL_EXTERIOR:
    case FaceCategory.STAIR:
    case FaceCategory.ROOM_WALL_EXTERIOR:
    case FaceCategory.FACADE_WALL_EXTERIOR:
    case FaceCategory.ROOM_WALL_BREP:
    case FaceCategory.ROOM_WALL_EXTRUSION:
    case FaceCategory.ROOM_WALL_INTERIOR:
    case FaceCategory.FOUNDATION_SLAB:
    case FaceCategory.FOUNDATION_STEM_WALL:
    case FaceCategory.PORCH_EXTERIOR:
    case FaceCategory.WALL_FRAMING:
    case FaceCategory.GABLE_END_WALL_FRAMING:
    case FaceCategory.COLUMN:
      return BSPPriority.DEFAULT;
    case FaceCategory.PROP_APPLIANCES:
    case FaceCategory.PROP_FIXTURES:
    case FaceCategory.PROP_FURNITURE:
    case FaceCategory.PROP_OTHER:
    case FaceCategory.PROP_FOLIAGE:
      assertUnreachable(undefined as never);
    // Site geometry - not used in BSP perspective rendering
    case FaceCategory.SITE_ROAD:
    case FaceCategory.SITE_GRASS:
    case FaceCategory.SITE_SIDEWALK:
    case FaceCategory.SITE_CURB:
    case FaceCategory.SITE_DRIVEWAY:
    case FaceCategory.SITE_FENCE:
    case FaceCategory.SITE_TOPOGRAPHY:
    case FaceCategory.SITE_WHITEBOX:
    case FaceCategory.SITE_PLANE:
      assertUnreachable(undefined as never);
    default:
      assertUnreachable(category);
  }
}

async function collectBuildingViewLazyPropMeshes({
  building,
  faceCategoryFilter,
  visibilitySelectors,
  entityTable = undefined,
  addEntityIds = false,
}: BuildingBSPNewOptions): Promise<LazyPropMesh[]> {
  if (faceCategoryFilter && propFaceCategories.every((cat) => !faceCategoryFilter.has(cat))) {
    return [];
  }
  const { meshes } = await collectBuildingMeshes({
    building,
    faceCategoryFilter,
    visibilitySelectors,
    findMaterials: true,
    entityTable,
    addEntityIds,
    meshNamingConvention: MeshNamingConvention.UNIQUE_NAMES,
  });
  return meshes.map((mesh) => ({ mesh, priority: PROPS_FACES_PRIORITY }));
}

export async function collectBuildingViewFaceGroups(
  options: BuildingBSPNewOptions,
): Promise<{ faceGroups: FaceGroupBSP[]; lazyPropMeshes: LazyPropMesh[] }> {
  const { groundGroups, groups, detailGroups } = collectBuildingViewFaceGroupBuckets(options);
  const lazyPropMeshes = await collectBuildingViewLazyPropMeshes(options);
  const faceGroups = [...groundGroups, ...groups, ...detailGroups];
  if (options.fillOpeningOccluders) {
    faceGroups.push(...buildOpeningOccluderFaceGroups(options.building));
  }
  return { faceGroups, lazyPropMeshes };
}

interface BuildingViewFaceGroupBuckets {
  groundGroups: FaceGroupBSP[];
  groups: FaceGroupBSP[];
  detailGroups: FaceGroupBSP[];
}

function collectBuildingViewFaceGroupBuckets({
  building,
  faceCategoryFilter,
  hatchCategoryFilter,
  enableHatchMaterials = true,
  entityTable = undefined,
  addEntityIds = false,
  selectedReportingUnitLabelId,
}: BuildingBSPNewOptions): BuildingViewFaceGroupBuckets {
  // visibilitySelectors is intentionally not forwarded — iterateBuildingGeometries does not consume
  // it. Prop visibility filtering is handled separately in collectBuildingViewLazyPropMeshes.
  const groups: FaceGroupBSP[] = [];
  const detailGroups: FaceGroupBSP[] = [];
  const groundGroups: FaceGroupBSP[] = [];

  const reportingUnitFaceFilter = selectedReportingUnitLabelId
    ? makeReportingUnitBuildingFaceFilter(building.doc, selectedReportingUnitLabelId)
    : undefined;
  const foundationReportingUnitResolver = reportingUnitFaceFilter
    ? getFoundationFaceReportingUnitResolver(building)
    : null;

  iterateBuildingGeometries(
    building,
    faceCategoryFilter ?? defaultIterateFaceCategories,
    lineRenderGeometryOptionsNew(addEntityIds, entityTable),
    ({
      faces,
      material,
      faceCategory,
      uvGen,
      materialTransformation,
      entityId,
      generateNeighbors,
      entityHandleForSelectingReportingUnit,
      reportingUnitLabelId: emittedReportingUnitLabelId,
    }) => {
      if (faceCategoryFilter && !faceCategoryFilter.has(faceCategory)) {
        return;
      }
      const reportingUnitLabelId =
        resolveFoundationFaceReportingUnitLabelId(
          foundationReportingUnitResolver,
          faceCategory,
          faces,
          emittedReportingUnitLabelId,
        ) ?? undefined;
      if (
        reportingUnitFaceFilter &&
        !reportingUnitFaceFilter({ entityHandleForSelectingReportingUnit, reportingUnitLabelId })
      ) {
        return;
      }
      const finalMaterial =
        enableHatchMaterials && (!hatchCategoryFilter || hatchCategoryFilter.has(faceCategory)) && material
          ? material
          : null;
      const priority = faceCategoryBSPPriority(faceCategory);
      let groupsToUse = groups;
      if (priority === BSPPriority.DETAIL) {
        groupsToUse = detailGroups;
      } else if (priority === BSPPriority.GROUND) {
        groupsToUse = groundGroups;
      }
      const faceGroup = faceGroupBSPNew(
        faces,
        finalMaterial,
        uvGen,
        materialTransformation,
        entityId,
        generateNeighbors,
        entityHandleForSelectingReportingUnit,
        reportingUnitLabelId,
      );
      groupsToUse.push(faceGroup);
    },
  );

  return { groundGroups, groups, detailGroups };
}

export async function buildingBSPNew(options: BuildingBSPNewOptions): Promise<SceneBSP | null> {
  const {
    building,
    faceCategoryFilter,
    visibilitySelectors,
    entityTable = undefined,
    addEntityIds = false,
    removeCoplanarLines = true,
    useBSPObjectPools = USE_BSP_OBJECT_POOLS_BY_DEFAULT,
    selectedReportingUnitLabelId,
  } = options;
  if (!building) {
    return null;
  }

  const oldBSPAllocators = getBSPAllocators();

  if (useBSPObjectPools) {
    const bspAllocators = new AllocatorsBSP();
    setBSPAllocators(bspAllocators);
  }

  const { groundGroups, groups, detailGroups } = collectBuildingViewFaceGroupBuckets(options);

  const isBuildingMirrored = building.mirror;
  const polygons: PolygonBSP[] = await buildPolygonsFromFaceGroups(
    groundGroups,
    GROUND_FACES_PRIORITY,
    isBuildingMirrored,
  );
  await buildPolygonsFromFaceGroups(groups, REGULAR_FACES_PRIORITY, isBuildingMirrored, polygons);
  await buildPolygonsFromFaceGroups(detailGroups, DETAIL_FACES_PRIORITY, isBuildingMirrored, polygons);

  if (
    !faceCategoryFilter ||
    propFaceCategories.some((v) => faceCategoryFilter.has(v)) ||
    doorFaceCategories.some((v) => faceCategoryFilter.has(v)) ||
    faceCategoryFilter.has(FaceCategory.DOOR_DECORATION) ||
    faceCategoryFilter.has(FaceCategory.WINDOW_DECORATION)
  ) {
    const reportingUnitFaceFilter = selectedReportingUnitLabelId
      ? makeReportingUnitBuildingFaceFilter(building.doc, selectedReportingUnitLabelId)
      : undefined;
    const propFilter = reportingUnitFaceFilter
      ? (prop: PropWithAssetId) => {
          // Props (especially room furniture) often lack their own reporting-unit handle, so
          // synthesize one from the host room — mirroring how the building renderer attributes props.
          const entityHandleForSelectingReportingUnit =
            prop.entityHandleForSelectingReportingUnit ?? roomEntityHandleForSelectingReportingUnit(prop.room);
          return reportingUnitFaceFilter({ entityHandleForSelectingReportingUnit });
        }
      : undefined;
    const { meshes, materials } = await collectBuildingMeshes({
      building,
      faceCategoryFilter,
      visibilitySelectors,
      findMaterials: true,
      entityTable,
      addEntityIds,
      meshNamingConvention: MeshNamingConvention.UNIQUE_NAMES,
      propFilter,
    });
    await buildPolygonsFromMeshesAndMaterials({
      meshes,
      materials,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      priority: PROPS_FACES_PRIORITY,
      polygons,
    });
  }

  const aabb = getPolygonsAabb(polygons, nonGroundPolygon);
  const root = buildBSPNode({ polygons });

  if (removeCoplanarLines) {
    removeCoplanarLinesBSP(root, polygons);
  }

  setBSPAllocators(oldBSPAllocators);

  return { aabb, root };
}

export function collectReportingUnitBuildingFaces(
  building: Building,
  faceCategoryFilter: Set<FaceCategory>,
  selectedReportingUnitLabelId: string,
): Face[] {
  const reportingUnitFaceFilter = makeReportingUnitBuildingFaceFilter(building.doc, selectedReportingUnitLabelId);
  const foundationReportingUnitResolver = getFoundationFaceReportingUnitResolver(building);
  const faces: Face[] = [];

  iterateBuildingGeometries(
    building,
    faceCategoryFilter,
    lineRenderGeometryOptionsNew(false, undefined),
    ({
      faces: groupFaces,
      faceCategory,
      entityHandleForSelectingReportingUnit,
      reportingUnitLabelId: emittedReportingUnitLabelId,
    }) => {
      const reportingUnitLabelId =
        resolveFoundationFaceReportingUnitLabelId(
          foundationReportingUnitResolver,
          faceCategory,
          groupFaces,
          emittedReportingUnitLabelId,
        ) ?? undefined;
      if (reportingUnitFaceFilter({ entityHandleForSelectingReportingUnit, reportingUnitLabelId })) {
        faces.push(...groupFaces);
      }
    },
  );

  return faces;
}

export function getRoofTopsBSP(
  roofs: GeneratedRoof[],
  useBSPObjectPools: boolean = USE_BSP_OBJECT_POOLS_BY_DEFAULT,
): SceneBSP {
  const oldBSPAllocators = getBSPAllocators();

  if (useBSPObjectPools) {
    const bspAllocators = new AllocatorsBSP();
    setBSPAllocators(bspAllocators);
  }

  const polygons = getRoofTopsPolygons(roofs);
  const root = buildBSPNode({ polygons });
  const aabb = getPolygonsAabb(polygons);
  removeCoplanarLinesBSP(root, polygons);

  setBSPAllocators(oldBSPAllocators);

  return { aabb, root };
}

export function getRoofTopsPolygons(roofs: GeneratedRoof[]): PolygonBSP[] {
  const polygons: PolygonBSP[] = [];
  for (const roof of roofs) {
    // Tags each roof's polygons so callers can filter the BSP back down to one roof. This is a
    // join key internal to the BSP, not a pick id; it pairs with the filter in editorViewerRoofUtils.
    const { roofTopPlates, trimsRoofMat, id: entityId } = roof;

    const faces: Face[] = [];
    for (let i = 0, l = roofTopPlates.length; i < l; i++) {
      const brepRoofTop = extrusionToBRep(roofTopPlates[i]);

      const [, topFace] = brepRoofTop.faces;

      faces.push(topFace);
    }

    for (const trimRoofMat of trimsRoofMat) {
      faces.push(...trimRoofMat.faces);
    }

    buildPolygonsFromFaces({
      faces,
      polygons,
      extendedOptions: {
        entityId,
      },
    });
  }

  return polygons;
}

export function buildPerspectiveLinesFinalMatrix({
  viewMatrix,
  prjMatrix,
}: Omit<RenderPerspectiveLinesOptions, "isElevation">): Mat4 {
  return mat4Mult(prjMatrix, viewMatrix);
}

interface RenderNodeContext {
  occlusionBSP: NodeBSP | null;
  renderedLines: RenderedPerspectiveLines;
}

function renderNodeContextNew(): RenderNodeContext {
  return {
    renderedLines: renderedPerspectiveLinesNew(),
    occlusionBSP: null,
  };
}

export function renderPerspectiveLines(options: RenderPerspectiveLinesOptions): RenderedPerspectiveLines {
  const {
    sceneBSP,
    occlusionBSP = null,
    roofVolumesBSP = null,
    viewMatrix,
    isPerspectiveMatrix,
    clippingPlanes,
    sectionClippingPlane,
    isElevation,
    epsilon = BSP_EPSILON,
    groundZ = 0,
    useBSPObjectPools = USE_BSP_OBJECT_POOLS_BY_DEFAULT,
  } = options;

  const roofVolumeContext = renderNodeContextNew();
  const defaultContext = renderNodeContextNew();

  const invViewMatrix = mat4Invert(viewMatrix);

  // Camera position
  const viewPosX = invViewMatrix[12];
  const viewPosY = invViewMatrix[13];
  const viewPosZ = invViewMatrix[14];

  // View direction
  const viewX = -viewMatrix[2];
  const viewY = -viewMatrix[6];
  const viewZ = -viewMatrix[10];

  const finalMatrix = buildPerspectiveLinesFinalMatrix(options);
  const sectionPolygons: RWTable<PolyLine2[]> = {};

  let sectionClippingPlaneBSP: PlaneBSP | null = null;

  if (sectionClippingPlane) {
    sectionClippingPlaneBSP = sectionClippingPlane ? planeBSPFlip(planeBSPFromPlane(sectionClippingPlane)) : null;
  }
  const screenGroundY = mat4Dot(finalMatrix, 0, 0, groundZ)[1];

  const planeXY = planeNewXY();

  const renderInfo: RenderInfoBSP = {
    isPerspectiveMatrix,
    viewX,
    viewY,
    viewZ,
    viewPosX,
    viewPosY,
    viewPosZ,
  };

  function renderLines({
    nodeBSP,
    plane,
    lines,
    dstLinesAboveGround,
    dstLinesBelowGround,
    neighborNormals = [],
    applySnap = false,
  }: {
    nodeBSP: NodeBSP | null;
    plane: PlaneBSP;
    lines: LineBSP[];
    dstLinesAboveGround: PolyLine2[];
    dstLinesBelowGround: PolyLine2[];
    neighborNormals?: Vec3[];
    applySnap?: boolean;
  }) {
    const neighborCount = neighborNormals.length;
    const snapTolerance = 2 * epsilon;
    const snapToleranceSq = snapTolerance * snapTolerance;

    if (neighborCount > 0) {
      if (!(neighborCount === lines.length)) {
        console.assert(false);
      }
    }

    const { nX, nY, nZ } = plane;
    const viewDot = vec3Dot(-viewX, -viewY, -viewZ, nX, nY, nZ);

    for (let i = 0, l = lines.length; i < l; i++) {
      const { x1, y1, z1, x2, y2, z2 } = lines[i];

      if (neighborCount > 0) {
        const neighborNormal = neighborNormals[i];

        const neighborNormalX = neighborNormal[0];
        const neighborNormalY = neighborNormal[1];
        const neighborNormalZ = neighborNormal[2];

        const threshold = 0.95;
        const dot = vec3Dot(nX, nY, nZ, neighborNormalX, neighborNormalY, neighborNormalZ);

        // If this is an edge between a front face and back face, it should be drawn.
        let edgeBetweenFrontFaceAndBackFace: boolean = false;

        if (isPerspectiveMatrix) {
          // Determine the facing direction of the two polygons in perspective view.
          const [eyeFromEdgeX, eyeFromEdgeY, eyeFromEdgeZ] = vec3Normalize(viewPosX - x1, viewPosY - y1, viewPosZ - z1);
          // We err on the side of rendering extra lines here, and the tolerance is fairly high. This ensures that lines are drawn where needed.
          // One good test case for this are the details on the garage door on the AltonCharles PDF plan set front page.
          const BIAS_TOWARDS_FRONT_FACING = 0.03;
          const normalBackFacing =
            vec3Dot(eyeFromEdgeX, eyeFromEdgeY, eyeFromEdgeZ, nX, nY, nZ) < BIAS_TOWARDS_FRONT_FACING;
          const neighborNormalBackFacing =
            vec3Dot(eyeFromEdgeX, eyeFromEdgeY, eyeFromEdgeZ, neighborNormalX, neighborNormalY, neighborNormalZ) <
            BIAS_TOWARDS_FRONT_FACING;
          edgeBetweenFrontFaceAndBackFace = normalBackFacing || neighborNormalBackFacing;
        } else {
          const neighborViewDot = vec3Dot(-viewX, -viewY, -viewZ, neighborNormalX, neighborNormalY, neighborNormalZ);
          edgeBetweenFrontFaceAndBackFace = neighborViewDot < DIST_EPSILON !== viewDot < DIST_EPSILON;
        }

        const isLineVisible = edgeBetweenFrontFaceAndBackFace || dot < threshold;

        if (!isLineVisible) {
          continue;
        }
      }

      const [screen1X, screen1Y, screen1Z] = mat4Dot(finalMatrix, x1, y1, z1);
      const [screen2X, screen2Y, screen2Z] = mat4Dot(finalMatrix, x2, y2, z2);

      if (vec2DistSquared(screen1X, screen1Y, screen2X, screen2Y) > DIST_EPSILON_SQUARED) {
        const segments: LineBSP[] = [];
        splitLineBSP({
          node: nodeBSP,
          coplanarsToFront: true,
          line: lineBSPNew(screen1X, screen1Y, screen1Z, screen2X, screen2Y, screen2Z),
          frontSegments: segments,
          epsilon,
        });

        // Snap the resulting segments end points to the corresponding source line points
        // Since splitLineBSP returns the segments in the order of the source line, we can
        // just snap the start vertex of the first segment and the end vertex of the last segment.
        if (applySnap && segments.length > 0) {
          const firstSegment = segments[0];
          if (vec2DistSquared(firstSegment.x1, firstSegment.y1, screen1X, screen1Y) < snapToleranceSq) {
            firstSegment.x1 = screen1X;
            firstSegment.y1 = screen1Y;
          }

          const lastSegment = segments[segments.length - 1];
          if (vec2DistSquared(lastSegment.x2, lastSegment.y2, screen2X, screen2Y) < snapToleranceSq) {
            lastSegment.x2 = screen2X;
            lastSegment.y2 = screen2Y;
          }
        }

        for (let j = 0, s = segments.length; j < s; j++) {
          const { x1: s0x, y1: s0y, x2: s1x, y2: s1y } = segments[j];

          if (!isElevation) {
            if (vec2DistSquared(s0x, s0y, s1x, s1y) > DIST_EPSILON_SQUARED) {
              dstLinesAboveGround.push(polyLine2ByTwoPoints(s0x, s0y, s1x, s1y));
            }
            continue;
          }

          // elevation drawings need to check if the line is split by the ground plane
          if (s0y <= screenGroundY && s1y <= screenGroundY) {
            if (vec2DistSquared(s0x, s0y, s1x, s1y) > DIST_EPSILON_SQUARED) {
              dstLinesBelowGround.push(polyLine2ByTwoPoints(s0x, s0y, s1x, s1y));
            }
          } else if (s0y >= screenGroundY && s1y >= screenGroundY) {
            if (vec2DistSquared(s0x, s0y, s1x, s1y) > DIST_EPSILON_SQUARED) {
              dstLinesAboveGround.push(polyLine2ByTwoPoints(s0x, s0y, s1x, s1y));
            }
          } else {
            const intersect = intersectLine2Line2(
              s0x,
              s0y,
              s1x,
              s1y,
              -100,
              screenGroundY,
              100,
              screenGroundY,
              undefined,
              undefined,
              false,
            );
            if (
              intersect instanceof CurveCurvePointIntersection &&
              vec2IsBetween(s0x, s0y, s1x, s1y, intersect.x, intersect.y, DIST_EPSILON)
            ) {
              const { x, y } = intersect;
              if (s0y <= screenGroundY) {
                if (vec2DistSquared(s0x, s0y, x, y) > DIST_EPSILON_SQUARED) {
                  dstLinesBelowGround.push(polyLine2ByTwoPoints(s0x, s0y, x, y));
                }
                if (vec2DistSquared(x, y, s1x, s1y) > DIST_EPSILON_SQUARED) {
                  dstLinesAboveGround.push(polyLine2ByTwoPoints(x, y, s1x, s1y));
                }
              }
              if (s1y <= screenGroundY) {
                if (vec2DistSquared(s1x, s1y, x, y) > DIST_EPSILON_SQUARED) {
                  dstLinesBelowGround.push(polyLine2ByTwoPoints(s1x, s1y, x, y));
                }
                if (vec2DistSquared(x, y, s0x, s0y) > DIST_EPSILON_SQUARED) {
                  dstLinesAboveGround.push(polyLine2ByTwoPoints(x, y, s0x, s0y));
                }
              }
            }
          }
        }
      }
    }
  }

  function renderNodeFunc(node: NodeBSP, side: number, renderNodeContext: RenderNodeContext, addLines: boolean) {
    const { polygons: nodePolygons } = node;
    const {
      aboveGroundLines,
      groundLines,
      aboveGroundHatchLines,
      groundHatchLines,
      belowGroundLines,
      belowGroundHatchLines,
    } = renderNodeContext.renderedLines;

    // skip view perpendicular polygons when rendering the cross section
    if (sectionClippingPlane && Math.abs(side) < BSP_EPSILON) {
      return;
    }

    const isBackPolygon = side > BSP_EPSILON;
    for (let p = 0, pl = nodePolygons.length; p < pl; p++) {
      const polygon = nodePolygons[p];
      const { plane } = polygon;
      let clippedPolygon = clippingPlanes ? clipPolygonOutsideConvexVol(polygon, clippingPlanes) : polygon;

      if (!clippedPolygon) {
        continue;
      }

      // extra plane clip when cross section is enabled
      if (sectionClippingPlaneBSP) {
        const { frontPolygon } = splitPolygonPlaneBSP(sectionClippingPlaneBSP, clippedPolygon);
        clippedPolygon = frontPolygon;

        if (!clippedPolygon) {
          continue;
        }
      }

      let projectedPolygon = projectPolygon(clippedPolygon, finalMatrix);

      if (!projectedPolygon) {
        continue;
      }

      // less agressive early discard, with an epsilon value slightly larger than 2 times the default epsilon
      const earlyDiscardEpsilon = 2.1 * epsilon;

      // TODO: nonOccludedPolygons have already gone through the occlusionBSP classification. Maybe we can use
      // the leaf node reached by each non occluded polygon, to speed up the update of the occlusionBSP
      const nonOccludedPolygons: PolygonBSP[] = [];
      splitPolygonBSP({
        node: renderNodeContext.occlusionBSP,
        polygon: projectedPolygon,
        coplanarsToFront: true,
        epsilon: earlyDiscardEpsilon,
        frontPolygons: nonOccludedPolygons,
      });

      // early discard the polygon if it is fully occluded and not a view perpendicular polygon
      // (always rendering the main lines from view perpendicular polygons to avoid some missing lines at corners)
      if (nonOccludedPolygons.length === 0 && Math.abs(side) >= BSP_EPSILON) {
        continue;
      }

      const { priority, extendedData } = clippedPolygon;

      if (extendedData && addLines) {
        const { visibleLines, splitLines, hatchLines: polygonHatchLines, neighborNormals } = extendedData;

        // back polygons don't render regular lines
        if (!isBackPolygon) {
          renderLines({
            nodeBSP: renderNodeContext.occlusionBSP,
            plane,
            lines: visibleLines,
            applySnap: true,
            dstLinesAboveGround: priority === GROUND_FACES_PRIORITY ? groundLines : aboveGroundLines,
            dstLinesBelowGround: priority === GROUND_FACES_PRIORITY ? groundLines : belowGroundLines,
            neighborNormals,
          });

          renderLines({
            nodeBSP: renderNodeContext.occlusionBSP,
            plane,
            lines: splitLines,
            applySnap: true,
            dstLinesAboveGround: priority === GROUND_FACES_PRIORITY ? groundLines : aboveGroundLines,
            dstLinesBelowGround: priority === GROUND_FACES_PRIORITY ? groundLines : belowGroundLines,
          });
        }

        // don't render hatch lines for view perpendicular polygons
        if (side < DIST_EPSILON) {
          // hatch lines are not snapped to the original line points. We don't need so much precision for them
          // and it's not worth the extra computation to snap them
          renderLines({
            nodeBSP: renderNodeContext.occlusionBSP,
            plane,
            lines: polygonHatchLines,
            dstLinesAboveGround: priority === GROUND_FACES_PRIORITY ? groundHatchLines : aboveGroundHatchLines,
            dstLinesBelowGround: priority === GROUND_FACES_PRIORITY ? groundHatchLines : belowGroundHatchLines,
          });
        }
      }

      // view perpendicular polygons don't update the occlusion tree
      if (Math.abs(side) < BSP_EPSILON) {
        continue;
      }

      if (isBackPolygon) {
        projectedPolygon = polygonBSPFlip(projectedPolygon);
      }

      // insert back polygons into the section polygon lists
      if (isBackPolygon && sectionClippingPlane) {
        const material = getPolygonMaterial(polygon);
        // We group prop polygons by prop to avoid passing overtly complex data to polygonUnion.
        const materialKey =
          (material ? materialCacheKey(material) : UNDEFINED_MATERIAL_KEY) + getPolygonPropId(polygon);
        if (sectionPolygons[materialKey] === undefined) {
          sectionPolygons[materialKey] = [];
        }

        const nonOccludedBackPolygons: PolygonBSP[] = [];
        splitPolygonBSP({
          node: renderNodeContext.occlusionBSP,
          polygon: projectedPolygon,
          coplanarsToFront: true,
          frontPolygons: nonOccludedBackPolygons,
          epsilon,
        });

        for (const { vertices } of nonOccludedBackPolygons) {
          const pl3 = polyLine3Close(polyLine3New(vertices));
          const pl2 = polyLine2SetCW(planeProjectPolyLine3(planeXY, pl3));

          if (polyLine2Area(pl2) < DIST_EPSILON_SQUARED) {
            continue;
          }

          sectionPolygons[materialKey].push(pl2);
        }
      }

      // update occlusion tree
      renderNodeContext.occlusionBSP = addOcclusionPolygonToBSP({
        node: renderNodeContext.occlusionBSP,
        isPerspectiveMatrix,
        polygon: projectedPolygon,
        zfar: 10000,
        epsilon,
      });
    }
  }

  if (roofVolumesBSP && sectionClippingPlane) {
    renderNodeBSP({
      node: roofVolumesBSP,
      renderInfo,
      order: TraversalOrderBSP.FRONT_TO_BACK,
      culling: TraversalCullingBSP.NONE,
      renderFunc: (node: NodeBSP, side: number) => renderNodeFunc(node, side, roofVolumeContext, true),
    });

    const projectedSectionClippingPlane = planeMoveAlongNormal(
      planeTransform(sectionClippingPlane, finalMatrix),
      2 * REMOVE_COPLANAR_LINES_EPSILON,
    );

    const projectedSectionClippingPlaneBSP = planeBSPFromPlane(projectedSectionClippingPlane);

    for (const materialKey in sectionPolygons) {
      const polygons = sectionPolygons[materialKey];

      if (polygons.length === 0) {
        continue;
      }

      for (const loop of polygons) {
        const loop3 = new Array<number>((loop.length / 2) * 3);
        for (let i = 0, j = 0, l = loop.length; i < l; i += 2, j += 3) {
          [loop3[j], loop3[j + 1], loop3[j + 2]] = planeEval(
            projectedSectionClippingPlane,
            loop[l - i - 2],
            loop[l - i - 1],
          );
        }

        const polygon = polygonBSPNew(0, loop3, projectedSectionClippingPlaneBSP);

        defaultContext.occlusionBSP = addOcclusionPolygonToBSP({
          node: defaultContext.occlusionBSP,
          isPerspectiveMatrix,
          polygon,
          zfar: 10000,
          epsilon: REMOVE_COPLANAR_LINES_EPSILON,
        });
      }
    }
  }

  const oldBSPAllocators = getBSPAllocators();

  if (useBSPObjectPools) {
    const bspAllocators = new AllocatorsBSP();
    setBSPAllocators(bspAllocators);
  }

  // This call only updates the occlusion volume, but it doesn't render any lines
  renderNodeBSP({
    node: occlusionBSP,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: sectionClippingPlane ? TraversalCullingBSP.NONE : TraversalCullingBSP.BACK,
    renderFunc: (node: NodeBSP, side: number) => renderNodeFunc(node, side, defaultContext, false),
  });

  // Now we render the lines for the main scene BSP
  renderNodeBSP({
    node: sceneBSP.root,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: sectionClippingPlane ? TraversalCullingBSP.NONE : TraversalCullingBSP.BACK,
    renderFunc: (node: NodeBSP, side: number) => renderNodeFunc(node, side, defaultContext, true),
  });

  renderCrossSectionPolygons({
    sceneBSP,
    sectionPolygons,
    sectionClippingPlane,
    renderedLines: defaultContext.renderedLines,
    viewPrjMatrix: finalMatrix,
  });

  setBSPAllocators(oldBSPAllocators);

  return defaultContext.renderedLines;
}

interface RenderCrossSectionPolygonsOptions {
  sceneBSP: SceneBSP;
  sectionPolygons: RWTable<PolyLine2[]>;
  sectionClippingPlane: Plane | undefined;
  renderedLines: RenderedPerspectiveLines;
  viewPrjMatrix: Mat4;
}

function renderCrossSectionPolygons({
  sceneBSP,
  sectionPolygons,
  sectionClippingPlane,
  renderedLines,
  viewPrjMatrix,
}: RenderCrossSectionPolygonsOptions) {
  const { crossSectionLines, crossSectionHatchLines } = renderedLines;

  const { aabb } = sceneBSP;

  if (sectionClippingPlane) {
    const backHatchMaterial = hatchProceduralMaterialNew(HatchMaterialId.Section_Lines);
    const boundingBSP = buildBSPNodeFromAABB3(aabb);
    const bigPL2 = polyLine2SetCW(polyLine2FromAABB2(aabb2New(-100000, -100000, 100000, 10000)));
    const bigFace = faceNew([bigPL2], sectionClippingPlane);
    const clippedBigFaces = clipFace({
      face: bigFace,
      options: {
        clippingNodes: [clippingNodeNew(boundingBSP, true)],
      },
    });

    let backHatchLines: LineBSP[] = [];
    if (clippedBigFaces.length > 0) {
      let hatchesAABB = polyLine2AABB2(clippedBigFaces[0].loops[0]);
      for (let i = 1, l = clippedBigFaces.length; i < l; i++) {
        hatchesAABB = aabb2Union(hatchesAABB, polyLine2AABB2(clippedBigFaces[i].loops[0]));
      }

      backHatchLines = fillHatchLines(backHatchMaterial, sectionClippingPlane, sectionClippingPlane, hatchesAABB);

      for (let i = 0, l = backHatchLines.length; i < l; i++) {
        const line = backHatchLines[i];
        const { x1, y1, z1, x2, y2, z2 } = line;

        [line.x1, line.y1, line.z1] = mat4Dot(viewPrjMatrix, x1, y1, z1);
        [line.x2, line.y2, line.z2] = mat4Dot(viewPrjMatrix, x2, y2, z2);
      }
    }

    for (const materialKey in sectionPolygons) {
      const polygons = sectionPolygons[materialKey];

      if (polygons.length === 0) {
        continue;
      }

      for (const polygon of polygons) {
        const polygonXY = new Array<number>((polygon.length / 2) * 3);
        for (let i = 0, j = 0, l = polygon.length; i < l; i += 2, j += 3) {
          polygonXY[j] = polygon[i];
          polygonXY[j + 1] = polygon[i + 1];
          polygonXY[j + 2] = 0;
        }

        const edgePlanes: PlaneBSP[] = buildPolygonEdgePlanes(polygonXY, 0, 0, -1);

        const clippedHatchLines = clipLinesOutsideConvexVol(backHatchLines, edgePlanes);
        for (let i = 0, l = clippedHatchLines.length; i < l; i++) {
          const { x1, y1, x2, y2 } = clippedHatchLines[i];
          crossSectionHatchLines.push(polyLine2ByTwoPoints(x1, y1, x2, y2));
        }
      }

      // re-join loops
      let unions: PolyLine2[] = [];
      try {
        polygonUnion(polygons.map((x) => [offsetPolyLine2(x, -POLYGON_OFFSET_FOR_CROSS_SECTION_UNION)])).forEach((x) =>
          unions.push(...x),
        );
        unions = unions.map((x) => offsetPolyLine2(x, POLYGON_OFFSET_FOR_CROSS_SECTION_UNION));
      } catch (e) {
        console.error(e);
        unions = polygons;
      }

      for (const union of unions) {
        if (polyLine2Area(union) < DIST_EPSILON_SQUARED) {
          continue;
        }

        for (let i = 0, l = polyLine2EdgeCount(union); i < l; i++) {
          crossSectionLines.push(polyLine2Edge(union, i));
        }
      }
    }
  }
}

export function renderPerspectivePolygons(options: RenderPerspectivePolygonsOptions): PolyLine2[] {
  const {
    sceneBSP,
    viewMatrix,
    isPerspectiveMatrix,
    clippingPlanes,
    polygonFilter,
    epsilon = BSP_EPSILON,
    useBSPObjectPools = USE_BSP_OBJECT_POOLS_BY_DEFAULT,
  } = options;

  const polygons: PolyLine2[] = [];

  let occlusionBSP: NodeBSP | null = null;

  const invViewMatrix = mat4Invert(viewMatrix);

  // Camera position
  const viewPosX = invViewMatrix[12];
  const viewPosY = invViewMatrix[13];
  const viewPosZ = invViewMatrix[14];

  // View direction
  const viewX = -viewMatrix[2];
  const viewY = -viewMatrix[6];
  const viewZ = -viewMatrix[10];

  const finalMatrix = buildPerspectiveLinesFinalMatrix(options);

  const renderInfo: RenderInfoBSP = {
    isPerspectiveMatrix,
    viewX,
    viewY,
    viewZ,
    viewPosX,
    viewPosY,
    viewPosZ,
  };

  function renderNodeFunc(node: NodeBSP, side: number) {
    const { polygons: nodePolygons } = node;

    // iterate all the polygons from this node
    for (let p = 0, pl = nodePolygons.length; p < pl; p++) {
      const polygon = nodePolygons[p];

      // clip the polygon against the view frustum volume (in world space)
      const clippedPolygon = clippingPlanes ? clipPolygonOutsideConvexVol(polygon, clippingPlanes) : polygon;
      if (!clippedPolygon) {
        continue;
      }

      // project the polygon
      const projectedPolygon = projectPolygon(clippedPolygon, finalMatrix);

      if (!projectedPolygon) {
        continue;
      }

      // clip the projected polygon against the occlusion BSP
      const frontPolygons: PolygonBSP[] = [];
      splitPolygonBSP({
        node: occlusionBSP,
        polygon: projectedPolygon,
        coplanarsToFront: false,
        frontPolygons,
      });

      // iterate the visible sub-polygons
      const projectedLoops: PolyLine2[] = [];
      for (const frontPolygon of frontPolygons) {
        const pts: number[] = [];
        const { vertices } = frontPolygon;

        let firstX: number = 0;
        let firstY: number = 0;

        // iterate the visible vertices to create the loop
        for (let i = 0, l = vertices.length; i < l; i += 3) {
          const screenX = vertices[i];
          const screenY = vertices[i + 1];

          if (i === 0) {
            firstX = screenX;
            firstY = screenY;
          }

          // add point to the loop
          pushXY(pts, screenX, screenY);
        }

        // close the loop
        pushXY(pts, firstX, firstY);

        // clean and enlarge the loop to improve the union algorithm
        const enlargedPoly = offsetPolyLine2(
          polyLine2SetCCW(polyLine2MergeCoincidentVertices(polyLine2New(pts))),
          -DIST_EPSILON,
        );

        // discard the loop if is a degenerated polygon
        if (polyLine2Area(enlargedPoly) < DIST_EPSILON_SQUARED) {
          continue;
        }

        // store the loop
        projectedLoops.push(enlargedPoly);

        // update the occlusion BSP with this visible sub-polygon
        if (Math.abs(side) >= BSP_EPSILON) {
          occlusionBSP = addOcclusionPolygonToBSP({
            node: occlusionBSP,
            isPerspectiveMatrix,
            polygon: frontPolygon,
            zfar: 10000,
            epsilon,
          });
        }
      }

      if (projectedLoops.length === 0 || (polygonFilter && !polygonFilter(polygon))) {
        continue;
      }

      // re-join loops
      try {
        const unions = polygonUnion(projectedLoops.map((x) => [offsetPolyLine2(x, DIST_EPSILON)])).map((x) => x[0]);

        polygons.push(...unions);
      } catch (e) {
        polygons.push(...projectedLoops);
      }
    }
  }

  const oldBSPAllocators = getBSPAllocators();

  if (useBSPObjectPools) {
    const bspAllocators = new AllocatorsBSP();
    setBSPAllocators(bspAllocators);
  }

  renderNodeBSP({
    node: sceneBSP.root,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: TraversalCullingBSP.BACK,
    renderFunc: renderNodeFunc,
  });

  setBSPAllocators(oldBSPAllocators);

  return polygons;
}

export function renderPerspectiveShadedPolygons(options: RenderPerspectiveShadedPolygonsOptions): ShadedPolygon[] {
  const {
    sceneBSP,
    viewMatrix,
    isPerspectiveMatrix,
    lightDirX,
    lightDirY,
    lightDirZ,
    lightIntensity = 1.0,
    ambientIntensity = 0.75,
    clippingPlanes,
    epsilon = BSP_EPSILON,
    useBSPObjectPools = USE_BSP_OBJECT_POOLS_BY_DEFAULT,
  }: RenderPerspectiveShadedPolygonsOptions = options;

  const shadedPolygons: ShadedPolygon[] = [];

  let occlusionBSP: NodeBSP | null = null;

  const invViewMatrix = mat4Invert(viewMatrix);

  // Camera position
  const viewPosX = invViewMatrix[12];
  const viewPosY = invViewMatrix[13];
  const viewPosZ = invViewMatrix[14];

  // View direction
  const viewX = -viewMatrix[2];
  const viewY = -viewMatrix[6];
  const viewZ = -viewMatrix[10];

  const finalMatrix = buildPerspectiveLinesFinalMatrix(options);

  const renderInfo: RenderInfoBSP = {
    isPerspectiveMatrix,
    viewX,
    viewY,
    viewZ,
    viewPosX,
    viewPosY,
    viewPosZ,
  };

  function renderNodeFunc(node: NodeBSP, side: number) {
    const { polygons: nodePolygons } = node;

    // iterate all the polygons from this node
    for (let p = 0, pl = nodePolygons.length; p < pl; p++) {
      const polygon = nodePolygons[p];

      // clip the polygon against the view frustum volume (in world space)
      const clippedPolygon = clippingPlanes ? clipPolygonOutsideConvexVol(polygon, clippingPlanes) : polygon;
      if (!clippedPolygon) {
        continue;
      }

      const { nX, nY, nZ } = polygon.plane;
      const shadingFactor = Math.min(
        Math.max(
          Math.min(Math.max(vec3Dot(nX, nY, nZ, -lightDirX, -lightDirY, -lightDirZ), 0), 1) * lightIntensity +
            ambientIntensity,
          0,
        ),
        1,
      );

      // project the polygon
      const projectedPolygon = projectPolygon(clippedPolygon, finalMatrix);

      if (!projectedPolygon) {
        continue;
      }

      // clip the projected polygon against the occlusion BSP
      const frontPolygons: PolygonBSP[] = [];
      splitPolygonBSP({
        node: occlusionBSP,
        polygon: projectedPolygon,
        coplanarsToFront: false,
        frontPolygons,
      });

      // iterate the visible sub-polygons
      const projectedLoops: PolyLine2[] = [];
      for (const frontPolygon of frontPolygons) {
        const pts: number[] = [];
        const { vertices } = frontPolygon;

        let firstX: number = 0;
        let firstY: number = 0;

        // iterate the visible vertices to create the loop
        for (let i = 0, l = vertices.length; i < l; i += 3) {
          const screenX = vertices[i];
          const screenY = vertices[i + 1];

          if (i === 0) {
            firstX = screenX;
            firstY = screenY;
          }

          // add point to the loop
          pushXY(pts, screenX, screenY);
        }

        // close the loop
        pushXY(pts, firstX, firstY);

        // clean and enlarge the loop to improve the union algorithm
        const enlargedPoly = offsetPolyLine2(
          polyLine2SetCCW(polyLine2MergeCoincidentVertices(polyLine2New(pts))),
          -DIST_EPSILON,
        );

        // discard the loop if is a degenerated polygon
        if (polyLine2Area(enlargedPoly) < DIST_EPSILON_SQUARED) {
          continue;
        }

        projectedLoops.push(enlargedPoly);

        // update the occlusion BSP with this visible sub-polygon
        if (Math.abs(side) >= BSP_EPSILON) {
          occlusionBSP = addOcclusionPolygonToBSP({
            node: occlusionBSP,
            isPerspectiveMatrix,
            polygon: frontPolygon,
            zfar: 10000,
            epsilon,
          });
        }
      }

      if (projectedLoops.length === 0) {
        continue;
      }

      // re-join loops
      let union: PolyLine2[];
      try {
        union = polygonUnion(projectedLoops.map((x) => [x])).map((x) => offsetPolyLine2(x[0], DIST_EPSILON));
      } catch (e) {
        union = projectedLoops;
      }

      // store the loops
      for (const pl of union) {
        shadedPolygons.push(shadedPolygonNew(pl, shadingFactor));
      }
    }
  }

  const oldBSPAllocators = getBSPAllocators();

  if (useBSPObjectPools) {
    const bspAllocators = new AllocatorsBSP();
    setBSPAllocators(bspAllocators);
  }

  renderNodeBSP({
    node: sceneBSP.root,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: TraversalCullingBSP.BACK,
    renderFunc: renderNodeFunc,
  });

  setBSPAllocators(oldBSPAllocators);

  return shadedPolygons;
}

function projectPolygon({ vertices, priority }: PolygonBSP, matrix: Mat4): PolygonBSP | null {
  const projectedVertices = new Array<number>(vertices.length);
  for (let i = 0, l = vertices.length; i < l; i += 3) {
    [projectedVertices[i], projectedVertices[i + 1], projectedVertices[i + 2]] = mat4Dot(
      matrix,
      vertices[i],
      vertices[i + 1],
      vertices[i + 2],
    );
  }

  if (projectedVertices.length < 9) {
    return null;
  }

  // build the projected polygon plane
  const projectedPolygonPlane = planeBSPByThreePoints(
    projectedVertices[0],
    projectedVertices[1],
    projectedVertices[2],
    projectedVertices[3],
    projectedVertices[4],
    projectedVertices[5],
    projectedVertices[6],
    projectedVertices[7],
    projectedVertices[8],
  );

  if (!projectedPolygonPlane) {
    return null;
  }

  return polygonBSPNew(priority, projectedVertices, projectedPolygonPlane);
}

interface BuildPolygonsFromMeshesAndMaterialsOptions {
  meshes: CollectedBuildingMesh[];
  materials: (MaterialSrc | undefined)[];
  uvGen?: UVGeneration;
  priority?: number;
  polygons?: PolygonBSP[];
}

async function buildPolygonsFromMeshesAndMaterials({
  meshes,
  materials,
  uvGen = UVGeneration.NONE,
  priority = 0,
  polygons = [],
}: BuildPolygonsFromMeshesAndMaterialsOptions): Promise<PolygonBSP[]> {
  if (!(materials.length === meshes.length)) {
    console.assert(false);
  }

  for (let i = 0, l = meshes.length; i < l; i++) {
    const mesh = meshes[i];
    const materialSrc = materials[i];

    const hatchMaterial = await defaultHatchMaterialFactory.getHatchMaterialForDesignMaterial(materials[i]);

    buildPolygonsFromMesh({
      mesh,
      designMaterialId: materialSrc,
      hatchMaterial,
      propId: mesh.name,
      useMeshUv: mesh.useMeshUv,
      uvGen,
      priority,
      polygons,
    });
  }

  return polygons;
}

function getPolygonMaterial(polygon: PolygonBSP): MaterialSrc | undefined {
  return polygon?.extendedData?.hatchProperties?.materialId;
}

function getPolygonPropId(polygon: PolygonBSP): string {
  return polygon?.extendedData?.propId ?? "";
}
